import React from 'react';
import {
  FaMapMarkerAlt,
  FaSearch,
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
} from 'react-icons/fa';

/**
 * RegionList Component
 * Displays a list of regions with search and filter functionality
 *
 * @param {Object} props - Component props
 * @param {Array} props.regions - Array of region objects
 * @param {boolean} props.loading - Loading state indicator
 * @param {string} props.error - Error message if any
 * @param {Function} props.onView - Callback for viewing a region
 * @param {Function} props.onEdit - Callback for editing a region
 * @param {Function} props.onDelete - Callback for deleting a region
 * @param {string} props.searchTerm - Current search term
 * @param {Function} props.onSearchChange - Callback for search term changes
 * @param {string} props.filterStatus - Current status filter
 * @param {Function} props.onFilterChange - Callback for filter changes
 */
const RegionList = ({
  regions = [],
  loading = false,
  error = null,
  onView,
  onEdit,
  onDelete,
  searchTerm = '',
  onSearchChange,
  filterStatus = '',
  onFilterChange,
}) => {
  const statusOptions = ['All', 'Active', 'Inactive'];

  if (loading) {
    return (
      <div className="eduss-region-list">
        <div className="eduss-region-list-loading">
          <div className="eduss-loading-spinner"></div>
          <p>Loading regions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="eduss-region-list">
        <div className="eduss-region-list-error">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  if (regions.length === 0) {
    return (
      <div className="eduss-region-list">
        <div className="eduss-region-list-empty">
          <FaMapMarkerAlt className="eduss-region-list-empty-icon" />
          <h3>No Regions Found</h3>
          <p>No regions match your current search criteria.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eduss-region-list">
      {/* Search and Filter Controls */}
      <div className="eduss-region-list-controls">
        <div className="eduss-region-list-search">
          <FaSearch className="eduss-region-list-search-icon" />
          <input
            type="text"
            placeholder="Search regions by name or ID..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="eduss-region-list-search-input"
          />
        </div>

        <div className="eduss-region-list-filter">
          <FaFilter className="eduss-region-list-filter-icon" />
          <select
            value={filterStatus}
            onChange={e => onFilterChange(e.target.value)}
            className="eduss-region-list-filter-select"
          >
            {statusOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Regions List */}
      <div className="eduss-region-list-content">
        {regions.map(region => (
          <div key={region.id} className="eduss-region-list-item">
            <div className="eduss-region-list-item-info">
              <div className="eduss-region-list-item-header">
                <h4>{region.name}</h4>
                <span className="eduss-region-list-item-id">
                  ID: {region.region_id}
                </span>
              </div>

              <div className="eduss-region-list-item-details">
                <div className="eduss-region-list-item-detail">
                  <strong>Description:</strong>{' '}
                  {region.description || 'No description provided'}
                </div>
                <div className="eduss-region-list-item-detail">
                  <strong>Status:</strong>
                  <span
                    className={`eduss-region-list-item-status ${region.status.toLowerCase()}`}
                  >
                    {region.status}
                  </span>
                </div>
                <div className="eduss-region-list-item-detail">
                  <strong>Created:</strong> {region.created_at}
                </div>
                <div className="eduss-region-list-item-detail">
                  <strong>Updated:</strong> {region.updated_at}
                </div>
              </div>
            </div>

            <div className="eduss-region-list-item-actions">
              <button
                onClick={() => onView(region)}
                className="eduss-region-list-item-btn view"
                title="View Region"
              >
                <FaEye />
              </button>
              <button
                onClick={() => onEdit(region)}
                className="eduss-region-list-item-btn edit"
                title="Edit Region"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => onDelete(region)}
                className="eduss-region-list-item-btn delete"
                title="Delete Region"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Results Summary */}
      <div className="eduss-region-list-summary">
        <p>
          Showing {regions.length} region{regions.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default RegionList;
